/**
 * 70.4 (the owner: "trees end up falling below fine terrain and can't really be seen easily. This is actually sort of cool
 * in snow, but not that cool in everything else"): a lying piece — a felled tree's trunk, a shed limb — rests on the
 * ground along its length. It lay flat on the plane of its foot (the stump's seat, the limb's middle), so wherever the
 * fine ground rose along it the wood went into the rise: story-1–4 with every kit tree felled, on soil the whole section
 * was under the ground at 13 % of the trunk's length, up to 4 people deep.
 *
 * Now it lies on the lowest line (measured at its middle) that is on or over the ground at points along it: a rigid log
 * on its highest supports. The ground is the one ground's `ground` level, less its material's sink (core/propSeat.ts
 * sinkAt — deep in snow, none on rock), as a standing thing is seated (71.2). It may cross a dip in the air between two
 * supports. Without the fine ground (classic, the relief's amplitude 0) there is no rest: the callers keep the flat lie,
 * bit for bit.
 */
import type { PlanetState, Vec3 } from '../types';
import type { WoodPiece } from '../constants/treeWood';
import { groundAt, type GroundDials } from './fineTerrain';
import { sinkAt } from './propSeat';
import { PERSON_BODY_HEIGHT } from './scaleProfile';
import { treeFrame } from './treeDraws';

/**
 * Where a lying piece rests, in its own frame (its foot's `up`, its axis along the ground): the line its underside lies
 * on, `y0` (world units over the foot's plane at the foot) and its rise along the axis as a turn (`cos`, `sin`).
 */
export interface LyingRest { y0: number; cos: number; sin: number }

/**
 * The supports tried along a piece: about one a person, at least its two ends and its middle, at most `supportsMax`.
 * Under two there is no rest — the piece lies flat, as before 70.4 (a probe's before arm: scratch fallenAB.ts).
 */
export const LYING_REST = { supportsMax: 9 };

/**
 * The rest of a piece lying from `s0` to `s1` (world units along `axis`, a unit tangent at `foot`, from the foot) on
 * `planet`'s fine ground. Null without the fine ground or with no length.
 */
export function lyingRest(planet: PlanetState, foot: Vec3, axis: Vec3, s0: number, s1: number, ps: GroundDials | null | undefined): LyingRest | null {
  if (!ps || !(ps.fineAmplitude > 0) || !(s1 > s0) || LYING_REST.supportsMax < 2) return null;
  const person = PERSON_BODY_HEIGHT * ps.k;
  const n = Math.min(LYING_REST.supportsMax, Math.max(3, Math.ceil((s1 - s0) / person) + 1));
  const s = new Float64Array(n), h = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const si = s0 + ((s1 - s0) * i) / (n - 1);
    const x = foot.x + axis.x * si - planet.position.x, y = foot.y + axis.y * si - planet.position.y, z = foot.z + axis.z * si - planet.position.z;
    const r = Math.sqrt(x * x + y * y + z * z), d = { x: x / r, y: y / r, z: z / r };
    s[i] = si;
    h[i] = groundAt(planet, d, 'ground', ps) - sinkAt(planet, d, ps) - r;   // (the support over the foot's plane there)
  }
  // the lowest line over every support at the middle: one through two of them (an upper hull's edge)
  const mid = (s0 + s1) / 2, eps = 1e-9 * person;
  let best = Infinity, y0 = 0, m = 0;
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
    const mm = (h[j] - h[i]) / (s[j] - s[i]), c = h[i] - mm * s[i];
    let over = true;
    for (let q = 0; q < n && over; q++) if (c + mm * s[q] < h[q] - eps) over = false;
    if (over && c + mm * mid < best) { best = c + mm * mid; y0 = c; m = mm; }
  }
  const len = Math.sqrt(1 + m * m);
  return { y0, cos: 1 / len, sin: m / len };
}

/**
 * A point of a lying piece from its foot (world): `x` across, `y` up from its underside and `z` along its axis (world units,
 * in the frame `toWorld` turns), tipped onto its rest.
 */
export function restPoint(toWorld: (x: number, y: number, z: number) => Vec3, rest: LyingRest, x: number, y: number, z: number): Vec3 {
  return toWorld(x, rest.y0 + y * rest.cos + z * rest.sin, z * rest.cos - y * rest.sin);
}

/**
 * A felled tree's rest: its fallen wood (the model's pieces lying along +z from the cut, scaled by `k`, turned to the fall
 * `yaw` about `up`) on the ground from its foot (the stump's seat). Null without the fine ground.
 */
export function fallenRest(planet: PlanetState, foot: Vec3, up: Vec3, yaw: number, k: number, fallen: readonly WoodPiece[], ps: GroundDials | null | undefined): LyingRest | null {
  if (!ps || !(ps.fineAmplitude > 0) || !fallen.length || LYING_REST.supportsMax < 2) return null;
  let z0 = Infinity, z1 = -Infinity;
  for (const p of fallen) { z0 = Math.min(z0, p[2], p[6]); z1 = Math.max(z1, p[2], p[6]); }
  return lyingRest(planet, foot, treeFrame(up, yaw)(0, 0, 1), z0 * k, z1 * k, ps);
}
